import { create } from "zustand";
import { GameConstant } from "@uno/protocol";

export type ChatMessage = {
    id: string;
    playerId: string;
    username: string;
    avatar: string;
    text: string;
    sentAt: number;
};

const MAX_MESSAGES = 50;
const COOLDOWN_MS = GameConstant.chatCooldownSeconds * 1000;

type State = {
    messages: Array<ChatMessage>;
    isOpen: boolean;
    inCooldown: boolean;
    unreadCount: number;
    typingPlayers: Record<string, number>;
};

type Actions = {
    open: () => void;
    close: () => void;
    push: (message: Omit<ChatMessage, "id" | "sentAt">) => void;
    triggerCooldown: () => void;
    setTyping: (playerId: string) => void;
    reset: () => void;
};

const useStoreRoomChat = create<State & Actions>((set) => ({
    messages: [],
    isOpen: false,
    inCooldown: false,
    unreadCount: 0,
    typingPlayers: {},

    open: () => set({ isOpen: true, unreadCount: 0 }),
    close: () => set({ isOpen: false }),

    push: (message) => {
        const entry: ChatMessage = {
            ...message,
            id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
            sentAt: Date.now(),
        };

        set((state) => {
            const updated = { ...state.typingPlayers };
            delete updated[message.playerId];

            return {
                messages: [...state.messages, entry].slice(-MAX_MESSAGES),
                unreadCount: state.isOpen ? 0 : state.unreadCount + 1,
                typingPlayers: updated,
            };
        });
    },

    triggerCooldown: () => {
        set({ inCooldown: true });
        setTimeout(() => set({ inCooldown: false }), COOLDOWN_MS);
    },

    setTyping: (playerId: string) => {
        const now = Date.now();

        set((state) => ({
            typingPlayers: { ...state.typingPlayers, [playerId]: now },
        }));

        setTimeout(() => {
            set((state) => {
                if(state.typingPlayers[playerId] === now) {
                    const updated = { ...state.typingPlayers };
                    delete updated[playerId];
                    return { typingPlayers: updated };
                }

                return state;
            });
        }, 3000);
    },

    reset: () => set({ messages: [], isOpen: false, inCooldown: false, unreadCount: 0, typingPlayers: {} }),
}));

export default useStoreRoomChat;
